import React, { useState } from 'react';
import { API_BASE_URL } from '../utils/api';
import { getCSRFTokenWithDebug, checkCookieAccess, getAllCSRFCookies } from '../utils/csrfDebug';

const CSRFDiagnostics = () => {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runDiagnostics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // First check local cookie access
      const cookieAccess = checkCookieAccess();
      const csrfTokenInfo = getCSRFTokenWithDebug();
      const allCSRFCookies = getAllCSRFCookies();

      // Run server-side diagnostics
      const response = await fetch(`${API_BASE_URL}/diagnostics/csrf-check`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const serverDiagnostics = await response.json();

      setDiagnostics({
        client: {
          cookieAccess,
          csrfTokenInfo,
          allCSRFCookies,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        },
        server: serverDiagnostics
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/diagnostics/csrf-refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        // Re-run diagnostics after refresh
        await runDiagnostics();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">CSRF Token Diagnostics</h2>
      
      <div className="mb-4 space-x-2">
        <button
          onClick={runDiagnostics}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Running...' : 'Run Diagnostics'}
        </button>
        
        <button
          onClick={refreshToken}
          disabled={loading}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
        >
          Force Refresh Token
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Error:</strong> {error}
        </div>
      )}

      {diagnostics && (
        <div className="space-y-6">
          <div className="bg-gray-100 p-4 rounded">
            <h3 className="font-bold text-lg mb-2">Client-Side Diagnostics</h3>
            
            <div className="mb-3">
              <h4 className="font-semibold">Cookie Access</h4>
              <pre className="bg-white p-2 rounded text-sm overflow-x-auto">
                {JSON.stringify(diagnostics.client.cookieAccess, null, 2)}
              </pre>
            </div>
            
            <div className="mb-3">
              <h4 className="font-semibold">CSRF Token Extraction</h4>
              <pre className="bg-white p-2 rounded text-sm overflow-x-auto">
                {JSON.stringify(diagnostics.client.csrfTokenInfo, null, 2)}
              </pre>
            </div>
            
            <div className="mb-3">
              <h4 className="font-semibold">All CSRF Cookies</h4>
              <pre className="bg-white p-2 rounded text-sm overflow-x-auto">
                {JSON.stringify(diagnostics.client.allCSRFCookies, null, 2)}
              </pre>
            </div>
          </div>

          <div className="bg-gray-100 p-4 rounded">
            <h3 className="font-bold text-lg mb-2">Server-Side Diagnostics</h3>
            <pre className="bg-white p-2 rounded text-sm overflow-x-auto">
              {JSON.stringify(diagnostics.server, null, 2)}
            </pre>
          </div>

          <div className="bg-blue-100 p-4 rounded">
            <h3 className="font-bold text-lg mb-2">Troubleshooting Steps</h3>
            <ol className="list-decimal list-inside space-y-2">
              <li>Check if cookies are enabled in the browser</li>
              <li>Clear browser cookies for this site and try logging in again</li>
              <li>If using Safari, check if "Prevent cross-site tracking" is disabled</li>
              <li>Try using a different browser to isolate browser-specific issues</li>
              <li>Check for browser extensions that might block cookies</li>
              <li>Ensure the site is not being accessed through a proxy that strips cookies</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSRFDiagnostics;
